// imageCompressor.js

// iOS-in-iframe detector (Safari/WebKit)
const isIOS = () =>
  /iP(ad|hone|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS
const inIframe = () => {
  try {
    return window.top !== window;
  } catch {
    return true;
  }
};

// On iOS Safari inside third-party iframes, blob: URLs often fail after reload.
// Prefer data: URLs there; keep blob: everywhere else.
const PREFER_VAULT_URL = isIOS() && inIframe();

// ---- converters (used by compressor and elsewhere) ----
export const dataURLtoBlob = (dataUrl) => {
  const [head, body] = dataUrl.split(',');
  const mime = (head.match(/data:(.*?);base64/) || [])[1] || 'application/octet-stream';
  const bin = atob(body);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

export const base64ToBlob = (data, mime = 'application/octet-stream') => {
  let base64 = data;
  let contentType = mime;

  if (data.startsWith('data:')) {
    const [meta, encoded] = data.split(',');

    const match = meta.match(/^data:(.*);base64$/);
    contentType = match ? match[1] : mime;

    base64 = encoded;
  }

  const bin = atob(base64);
  const len = bin.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = bin.charCodeAt(i);
  }

  return new Blob([bytes], { type: contentType });
};

export const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

// ============================================================================
// Image compression utilities (target ≤ ~700KB, webp with fallback to jpeg)
// ============================================================================
export const IMG_TARGET_MAX_BYTES = 700 * 1024; // ~700KB
const IMG_MAX_DIMENSION = 2000; // clamp longest side
const IMG_INIT_QUALITY = 0.86;
const IMG_MIN_QUALITY = 0.55;
const IMG_QUALITY_STEP = 0.08;

// Draw a blob onto a canvas, downscaling if needed
const drawBlobToCanvas = async (blob, maxDim = IMG_MAX_DIMENSION) => {
  let drawable = null;
  let width = 0;
  let height = 0;

  try {
    const bmp = await createImageBitmap(blob);
    drawable = bmp;
    width = bmp.width;
    height = bmp.height;
  } catch {
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = url;
      });
      drawable = img;
      width = img.naturalWidth || img.width;
      height = img.naturalHeight || img.height;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  const scale = Math.min(1, maxDim / Math.max(width, height));
  const cw = Math.max(1, Math.round(width * scale));
  const ch = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d', { alpha: true });
  ctx.drawImage(drawable, 0, 0, cw, ch);
  return canvas;
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

export const compressImageBlob = async (
  sourceBlob,
  {
    targetBytes = IMG_TARGET_MAX_BYTES,
    maxDimension = IMG_MAX_DIMENSION,
    initialQuality = IMG_INIT_QUALITY,
    minQuality = IMG_MIN_QUALITY,
    step = IMG_QUALITY_STEP,
  } = {},
) => {
  const isHeic = /image\/(heic|heif)/i.test(sourceBlob.type || '');
  if (sourceBlob.size <= targetBytes && !isHeic) return sourceBlob;

  const canvas = await drawBlobToCanvas(sourceBlob, maxDimension);

  // iOS Safari often returns null for 'image/webp' encodes; prefer JPEG there.
  const order = isIOS() ? ['image/jpeg', 'image/webp'] : ['image/webp', 'image/jpeg'];

  let out = null;
  let chosenType = order[0];
  let quality = initialQuality;

  for (const type of order) {
    chosenType = type;

    // First try canvas.toBlob; Safari may return null → fall back to toDataURL.
    const attempt = async (q) => {
      let blob = await canvasToBlob(canvas, type, q);
      if (!blob) {
        try {
          const dataUrl = canvas.toDataURL(type, q);
          blob = dataURLtoBlob(dataUrl);
        } catch {
          /* ignore */
        }
      }
      return blob;
    };

    out = await attempt(quality);
    if (!out) continue;

    while (out.size > targetBytes && quality > minQuality) {
      quality = Math.max(minQuality, quality - step);
      const next = await attempt(quality);
      if (!next) break;
      out = next;
    }

    // We got a valid blob for this type — stop trying others.
    break;
  }

  if (!out) return sourceBlob;

  // Ensure a good MIME (some WebKit paths yield octet-stream)
  if (!out.type || out.type === 'application/octet-stream') {
    out = new Blob([await out.arrayBuffer()], { type: chosenType });
  }

  return out;
};

// Turn any {base64|url|src} image payload into a compressed payload (idempotent)
export const ensureCompressedImage = async (img) => {
  try {
    if (img?.__compressed) return img;

    // Build a source blob from whatever the payload provides.
    let srcBlob = null;

    if (img.base64) {
      // iOS camera often gives plain base64 (no header) — handle both forms.
      if (typeof img.base64 === 'string' && img.base64.startsWith('data:')) {
        srcBlob = dataURLtoBlob(img.base64);
      } else {
        const mimeHint = img.mime || img.type || 'image/jpeg';
        srcBlob = base64ToBlob(img.base64, mimeHint);
      }
    } else if (img.url && img.url.startsWith('blob:')) {
      srcBlob = await (await fetch(img.url)).blob();
    } else if (img.src && img.src.startsWith('blob:')) {
      srcBlob = await (await fetch(img.src)).blob();
    }

    if (!srcBlob) return img;

    const origBytes = srcBlob.size;

    // Force conversion for HEIC/HEIF and make sure iOS gets JPEG when needed.
    const comp = await compressImageBlob(srcBlob);
    const heic = /image\/(heic|heif)/i.test(srcBlob.type || '');
    const finalBlob = comp && (comp.size <= srcBlob.size || heic) ? comp : srcBlob;

    const base64 = await blobToDataURL(finalBlob);
    const blobUrl = PREFER_VAULT_URL ? null : URL.createObjectURL(finalBlob);

    return {
      ...img,
      base64,
      url: PREFER_VAULT_URL ? base64 : blobUrl, // ✅ iOS-in-iframe → data:
      src: PREFER_VAULT_URL ? base64 : blobUrl,
      size: finalBlob.size,
      __compressed: true,
      __origBytes: origBytes,
      __fmt: finalBlob.type || 'image/jpeg',
    };
  } catch (e) {
    console.warn('[Image] Compression failed, using original image', e);
    return img;
  }
};
